/**
 * ============================================================
 * MONTE HERMOM | PERFIL CARD SOBERANO
 * ============================================================
 * ARQUIVO: /src/components/perfil/Perfil_Card_soberano.jsx
 * VERSÃO:  2.0.0 (compacto + lucide-react)
 *
 * MUDANÇAS v2.0.0:
 *   ✔ Altura: ~400px → ~110px
 *   ✔ Avatar: 112px → 40px (com inicial)
 *   ✔ Stats inline (3 números em linha)
 *   ✔ Chancela: badge compacto no canto
 *   ✔ Popover de "Soberania" reposicionado
 *   ✔ Ícones migrados para lucide-react
 * ============================================================
 */

import React, { useState } from 'react';
import {
  ShieldCheck,
  FileCheck,
  Briefcase,
  Fingerprint,
  Share2,
  X,
} from 'lucide-react';

/* ---------- Hierarquia de chancelas ---------- */

const CHANCELAS = [
  { key: 'auth2fa',      icon: ShieldCheck, label: '2FA',         color: 'bg-emerald-600', nivel: 'N5' },
  { key: 'documental',   icon: FileCheck,   label: 'Documental',  color: 'bg-rose-900',    nivel: 'N4' },
  { key: 'profissional', icon: Briefcase,   label: 'Profissional',color: 'bg-blue-900',    nivel: 'N3' },
  { key: 'identidade',   icon: Fingerprint, label: 'Identidade',  color: 'bg-slate-700',   nivel: 'N2' },
  { key: 'social',       icon: Share2,      label: 'Social',      color: 'bg-blue-600',    nivel: 'N1' },
];

const Perfil_Card_soberano = ({ userData, tenantData }) => {
  const [exibirPopover, setExibirPopover] = useState(false);

  /* ---------- Chancela ativa (a maior conquistada) ---------- */
  const chancelaAtiva = CHANCELAS.find(c => userData?.[c.key]) || null;

  /* ---------- Inicial do nome para o avatar ---------- */
  const inicial = (userData?.nome || 'U').charAt(0).toUpperCase();

  return (
    <div className="relative">

      {/* =========================================================
          LINHA 1: Avatar + Nome + Chancela + Instância
      ========================================================= */}
      <div className="flex items-center gap-3">

        {/* Avatar compacto com inicial */}
        <div className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800
                        flex items-center justify-center shrink-0">
          <span className="text-white font-black text-sm">{inicial}</span>
        </div>

        {/* Nome + cargo */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-black uppercase text-[11px] tracking-wider
                        leading-tight truncate">
            {userData?.nome || 'Usuário'}
          </p>
          <p className="text-slate-500 font-black text-[8px] uppercase tracking-widest
                        leading-tight truncate mt-0.5">
            {userData?.cargo || 'Perito'}
            {tenantData?.nome && (
              <span className="text-slate-600"> · {tenantData.nome}</span>
            )}
          </p>
        </div>

        {/* Chancela compacta */}
        {chancelaAtiva && (
          <button
            type="button"
            title={`Chancela ${chancelaAtiva.label} (${chancelaAtiva.nivel})`}
            className={`w-8 h-8 rounded-lg ${chancelaAtiva.color}
                        flex items-center justify-center shrink-0
                        hover:scale-110 transition-transform`}
          >
            <chancelaAtiva.icon size={14} className="text-white" />
          </button>
        )}
      </div>

      {/* =========================================================
          LINHA 2: Soberania + Stats inline
      ========================================================= */}
      <div className="mt-2.5 flex items-center justify-between gap-2 text-[9px]">

        {/* Botão Soberania — abre popover */}
        <button
          type="button"
          onClick={() => setExibirPopover(v => !v)}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md
                     bg-slate-950/60 border border-rose-900/30
                     hover:border-rose-700/50 transition-colors"
          title="Sobre a Soberania"
        >
          <span className="w-1 h-1 rounded-full bg-rose-500 animate-pulse" />
          <span className="text-rose-400 font-black tracking-wider">
            {userData?.pontos || 0} PTS
          </span>
        </button>

        {/* Stats inline */}
        <div className="flex items-center gap-2 text-slate-500 font-black tracking-wider">
          <span title="Abertos">
            <span className="text-rose-500">{userData?.abertos || 0}</span>
            <span className="text-slate-600 ml-0.5">AB</span>
          </span>
          <span className="text-slate-700">·</span>
          <span title="Concluídos">
            <span className="text-white">{userData?.concluidos || 0}</span>
            <span className="text-slate-600 ml-0.5">CO</span>
          </span>
          <span className="text-slate-700">·</span>
          <span title="Em revisão">
            <span className="text-rose-900">{userData?.revisao || 0}</span>
            <span className="text-slate-600 ml-0.5">RE</span>
          </span>
        </div>
      </div>

      {/* =========================================================
          POPOVER EXPLICATIVO (soberania)
      ========================================================= */}
      {exibirPopover && (
        <div
          className="absolute left-0 right-0 top-full mt-2 z-40
                     bg-slate-900 border-2 border-rose-900/50 rounded-2xl p-4
                     shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        >
          {/* Header do popover */}
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-black text-white text-[10px] uppercase tracking-widest
                           border-l-2 border-rose-700 pl-2">
              Soberania M. Hermom
            </h4>
            <button
              type="button"
              onClick={() => setExibirPopover(false)}
              className="text-slate-500 hover:text-white transition-colors"
            >
              <X size={12} />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="space-y-2 text-[9px] leading-relaxed text-slate-400">
            <p>
              <span className="text-white font-black">IMPORTÂNCIA:</span>{' '}
              Define o peso das suas homologações e acesso a dados sensíveis da instância.
            </p>
            <p>
              <span className="text-rose-500 font-black">COMO EVOLUIR:</span>{' '}
              Habilite as 5 chancelas, cumpra vistorias da agenda e garanta a precisão estatística.
            </p>
          </div>

          {/* Lista de chancelas */}
          <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-1.5">
            {CHANCELAS.map(c => {
              const ativa = userData?.[c.key];
              return (
                <div
                  key={c.key}
                  title={`${c.label} (${c.nivel})${ativa ? ' · ativa' : ' · inativa'}`}
                  className={`w-6 h-6 rounded-md flex items-center justify-center ${
                    ativa ? c.color : 'bg-slate-800'
                  }`}
                >
                  <c.icon size={11} className={ativa ? 'text-white' : 'text-slate-600'} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Perfil_Card_soberano;